import './InfoCardBodyGraph.scss';
import dayjs from 'dayjs';
import {
  ChartOptions,
  ComposedChart,
  PendingContent,
  Spinner,
  Status,
} from 'plume-ui';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  API_STANDARD_DATE_TIME_TOP_OF_MINUTE,
  LONG_DATE_TIME_FORMAT,
  SHORT_DATE_FORMAT,
} from '../../../../../config';
import {
  AdjustableComponent,
  useClasses,
} from '../../../../../utils/hooks/useClasses';
import { Monitor } from '../../../types';
import DownloadUnresolvedListsButton from './DownloadUnresolvedListsButton';
import InfoCardBody, { InfoCardBodyProps } from './InfoCardBody';

type InfoCardBodyGraphProps = {
  label: string;
  data: ChartOptions.ComposedChartDataItem[];
  groups: ChartOptions.ComposedChartGroup[];
  curveType?:
  | 'basis'
  | 'basisClosed'
  | 'basisOpen'
  | 'linear'
  | 'linearClosed'
  | 'monotone'
  | 'monotoneX'
  | 'monotoneY'
  | 'natural'
  | 'step'
  | 'stepAfter'
  | 'stepBefore';
  bodyProps?: Partial<InfoCardBodyProps>;
  loading?: boolean;
  tickFormatter?: (value: any, index: number) => string;
  monitor?: Monitor;
  daysBack?: number;
};

type InfoCardBodyGraphStyles = {
  root: string;
  legend: string;
  legendItem: string;
};

const InfoCardBodyGraph: AdjustableComponent<
  InfoCardBodyGraphProps,
  InfoCardBodyGraphStyles
> = ({
  label,
  groups,
  data,
  curveType,
  classes,
  bodyProps,
  loading = false,
  monitor,
  daysBack,
  tickFormatter,
}) => {
    const { t } = useTranslation();

    const [throttledTooltip, setThrottledTooltip] = useState(true);

    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedDateLocationCount, setSelectedDateLocationCount] = useState<
      number
    >(0);

    const styles: InfoCardBodyGraphStyles = useClasses(
      {
        root: 'MonitorInfo__info-card__graph',
        legend: 'MonitorInfo__info-card__graph__legend',
        legendItem: 'MonitorInfo__info-card__graph__legend-item',
      },
      classes,
    );

    useEffect(() => {
      setThrottledTooltip(true);
      setSelectedDate('');
      setTimeout(() => {
        setThrottledTooltip(false);
      }, 300);
    }, [loading]);

    return (
      <InfoCardBody {...bodyProps} label={label} showMenu>
        <PendingContent loading={loading} loader={Spinner}>
          <div className={styles.root}>
            {selectedDate && (
              <div className={'MonitorInfo__info-card__graph__download-card'}>
                <div className="MonitorInfo__info-card__graph__download-card__text">
                  <div>
                    {t('monitors.monitorInfo.downloadLocationDataFor', {
                      date: (dayjs(selectedDate).isValid()
                        ? dayjs(selectedDate)
                        : dayjs(selectedDate, 'HH:mm MMM DD YYYY').isValid()
                          ? dayjs(selectedDate, 'HH:mm MMM DD YYYY')
                          : dayjs(selectedDate, 'HH:mm A MMM DD YYYY')
                      ).format(
                        (daysBack || 0) >= 7
                          ? SHORT_DATE_FORMAT
                          : LONG_DATE_TIME_FORMAT,
                      ),
                    })}
                  </div>
                  <div className="MonitorInfo__info-card__graph__download-card__subtitle">
                    {t('monitors.monitorInfo.numLocations', {
                      count: selectedDateLocationCount,
                    })}
                  </div>
                </div>
                <div>
                  <DownloadUnresolvedListsButton
                    monitor={monitor}
                    key={monitor?.id}
                    locationCount={selectedDateLocationCount}
                    disabled={!monitor}
                    isDaily={daysBack && daysBack >= 7 ? true : false}
                    timestamp={(dayjs(selectedDate).isValid()
                      ? dayjs(selectedDate)
                      : dayjs(selectedDate, 'HH:mm MMM DD YYYY').isValid()
                        ? dayjs(selectedDate, 'HH:mm MMM DD YYYY')
                        : dayjs(selectedDate, 'HH:mm A MMM DD YYYY')
                    )
                      .utc()
                      .format(API_STANDARD_DATE_TIME_TOP_OF_MINUTE)}
                  />
                </div>
              </div>
            )}
            {!selectedDate && (
              <div className="MonitorInfo__info-card__graph__selectDateInfo">
                {t('monitors.monitorInfo.clickGraphToSelectDate')}
              </div>
            )}
            {!!data.length && (
              <ComposedChart
                curveType={curveType || 'monotoneX'}
                data={data}
                groups={groups}
                config={{
                  showTooltipTitleForNumerical: true,
                  showPercentageOnTooltip: false,
                  showVerticalCartesianGrid: true,
                  xAxisLabelAngle: 0,
                  xAxisPadding: { left: 38, right: 38 },
                  minHeight: 250,
                  xAxisHeightOverride: 40,
                  allowDecimalsY: false,
                }}
                stacked
                showDots={false}
                onChartClick={(item) => {
                  if (item.activeLabel) {
                    setSelectedDate(
                      `${item.activeLabel} ${dayjs().format('YYYY')}`,
                    );
                    setSelectedDateLocationCount(
                      item.activePayload?.[0]?.value || 0,
                    );
                  }
                }}
                tickFormatter={
                  tickFormatter
                    ? tickFormatter
                    : (value: any, index: number): string =>
                      0 === Math.round(index % 7) ? value : ''
                }
                customTooltip={throttledTooltip ? <></> : undefined}
              />
            )}
            <div className={styles.legend}>
              {groups.map((group) => (
                <div className={styles.legendItem} key={`${group.stroke}`}>
                  <Status
                    color={group.stroke}
                    label={group.label}
                    variant="legend"
                  />
                </div>
              ))}
            </div>
          </div>
        </PendingContent>
      </InfoCardBody>
    );
  };

export default memo(InfoCardBodyGraph);
